import type { LayerKind } from "@/layers/utility/createLayerObject";
import { ParamModelSelect } from "@/overlay/components/SmartForm/HookInputs/ParamModelSelect";
import { SelectInput } from "@/overlay/components/SmartForm/HookInputs/SelectInput";
import { ModeSwitchableForm } from "@/overlay/components/SmartForm/SmartFormUtils/FormComponents/ModeSwitchableFormProps";
import { useFormWithUtils } from "@/overlay/components/SmartForm/SmartFormUtils/FormHooks/useFormUtils";
import { usePartialWeatherParameter } from "@/overlay/components/SmartForm/SmartFormUtils/FormHooks/usePartialWeatherParameter";
import { usePartialWeatherParameterModels } from "@/overlay/components/SmartForm/SmartFormUtils/FormHooks/usePartialWeatherParameterModels";
import type { BaseFormProps } from "@/overlay/components/SmartForm/SmartFormUtils/commonTypeUtils";
import { getStandardSearchEngine, searchEngineWeatherModels } from "@/weather-parameters";
import { t } from "@lingui/core/macro";
import {
  SoundingParamType,
  type TephigramCreate,
  type TephigramSchemaOutput,
} from "@mm/metx-workbench.meteomatics.com";
import type { HasLayerIdxNarrowing, PartialWeatherParameter } from "@mm/weather-parameter-utils";
import { useEffect, useState } from "react";

type TephigramPatch = Partial<TephigramCreate | TephigramSchemaOutput>;

/**
 * Form data passed from the child components.
 */
export type TephigramToolCreateFormData = {
  layerData: {
    model: string;
    levelUnit: string;
    soundingType: keyof typeof SoundingParamType;
  };
};

type TephigramToolCreateFormProps = {
  defaultFormData: TephigramPatch;
} & BaseFormProps<TephigramToolCreateFormData>;
const modelSearchEngine = searchEngineWeatherModels;

export const tephigramUnitList: { value: any; label: string | number }[] = [
  { value: "hPa", label: "Hectopascals" },
  { value: "m", label: "Meters" },
  { value: "ft", label: "Feet" },
];

export const soundingTypeOptions = [
  { value: SoundingParamType.MU, label: "Most Unstable (MU)" },
  { value: SoundingParamType.ML, label: "Mixed Layer (ML)" },
  { value: SoundingParamType.SB, label: "Surface Based (SB)" },
];

export const tephigramModels = ["mix", "ecmwf-ifs", "dwd-icon-global", "ncep-gfs", "mm-euro1k"];

function Form({
  parameter,
  onSubmit,
  submitMode,
  onCancel,
  defaultFormData,
}: TephigramToolCreateFormProps & { parameter: HasLayerIdxNarrowing<PartialWeatherParameter> }) {
  const { formMethods, formValName } = useFormWithUtils<TephigramToolCreateFormData>({});
  const availableModels = usePartialWeatherParameterModels(parameter, modelSearchEngine).filter((modelSchema) =>
    tephigramModels.includes(modelSchema.identifier),
  );

  return (
    <ModeSwitchableForm onSubmit={formMethods.handleSubmit(onSubmit)} onCancel={onCancel} submitMode={submitMode}>
      <ParamModelSelect
        instruction={{
          currValue: defaultFormData.model,
          formMethods: formMethods,
          formValueName: formValName("layerData.model"),
        }}
        availableModels={availableModels}
      />
      <SelectInput
        label={t`level unit`}
        instruction={{
          currValue: defaultFormData.levelUnit,
          formValueName: formValName("layerData.levelUnit"),
          formMethods,
        }}
        values={tephigramUnitList}
      />
      <SelectInput
        label={t`sounding type`}
        instruction={{
          currValue: defaultFormData.soundingType,
          formValueName: formValName("layerData.soundingType"),
          formMethods,
        }}
        values={soundingTypeOptions}
      />
    </ModeSwitchableForm>
  );
}

export function TephigramToolUpdateForm(props: TephigramToolCreateFormProps) {
  const [parameter, setParameter] = useState<HasLayerIdxNarrowing<PartialWeatherParameter>>();

  // TODO: filter model somehow
  const { search } = usePartialWeatherParameter("t_2m:C", "PlotParameter" as LayerKind, getStandardSearchEngine());

  useEffect(() => {
    search().then(setParameter);
  }, [search]);

  useEffect(() => {
    search().then(setParameter);
  }, [search]);

  // We need to keep parameter async, because of old workaround, introduced inside "search" function
  // Initial problem is, that there is no distinguishable difference between wind_speed_<measure>_<level>_<interval>:<unit>
  // and wind_speed_<measure>_<height>_<interval>:<unit>, if height values matches values inside level.
  // Workaround is - if multiple possible matches are returned, we always use second one (because measure regex is put after level)
  // This kinda works, but isn't long term solution and prevent's us to use "matchSomeParameterNameExactly" from search engine.
  // To simplify async operation, form is created only when search engine returns parameter to not complicate hooks more
  if (parameter) {
    return <Form {...props} parameter={parameter} />;
  }

  return <></>;
}
